import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowUp, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import nexisLogo from "@/assets/nexis-logo-new.png";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import clipaiLogo from "@/assets/clipai-logo-new.png";
import keywordLogo from "@/assets/keyword-research-logo-new.png";
import emailLogo from "@/assets/email-list-builder-logo-new.png";
import salesLogo from "@/assets/sales-page-builder-logo-new.png";
import launchLogo from "@/assets/launch-advisor-logo-new.png";
import askfintLogo from "@/assets/askfint-logo-new.png";

const LAUNCH_DATE = import.meta.env.VITE_LAUNCH_DATE || "2026-04-15T00:00:00Z";

const tools = [
  { logo: nexisLogo, name: "Nexis AI", desc: "Generate viral video scripts in seconds" },
  { logo: monetizationLogo, name: "Monetisation AI", desc: "Build and package digital products with AI" },
  { logo: clipaiLogo, name: "Clip AI", desc: "Turn long videos into short viral clips" },
  { logo: keywordLogo, name: "Keyword Research", desc: "Find trending topics with real data" },
  { logo: emailLogo, name: "Email List Builder", desc: "Create email funnels that convert" },
  { logo: salesLogo, name: "Sales Page Builder", desc: "Generate high-converting sales pages" },
  { logo: launchLogo, name: "Launch Advisor", desc: "Get a personalised 14-day launch plan" },
  { logo: askfintLogo, name: "AskFint", desc: "Your AI business mentor, available 24/7" },
];

function useCountdown(targetDate: string) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return timeLeft;
}

export default function Waitlist() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const countdown = useCountdown(LAUNCH_DATE);

  const refCode = searchParams.get("ref");
  const utmSource = searchParams.get("utm_source");

  const getSource = (): string => {
    if (refCode) return "referral";
    if (utmSource === "tiktok") return "tiktok_ad";
    if (utmSource === "meta") return "meta_ad";
    return "organic";
  };

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (!isValidEmail(email.trim())) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
      setEmailError("Email addresses do not match.");
      return;
    }
    setEmailError("");
    setSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("waitlist")
        .insert({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          source: getSource(),
          referred_by: refCode || null,
        })
        .select("referral_code")
        .single();

      if (error) {
        if (error.code === "23505") {
          toast.error("This email is already on the waitlist!");
        } else {
          throw error;
        }
        return;
      }

      if (refCode) {
        await supabase.rpc("increment_referral_count", { p_referral_code: refCode });
      }

      try {
        await supabase.functions.invoke("send-waitlist-email", {
          body: { name: name.trim(), email: email.trim().toLowerCase(), referralCode: data.referral_code },
        });
      } catch {
        // Non-blocking
      }

      setSubmitted(true);
      navigate(`/waitlist/confirmed?ref=${data.referral_code}`);
    } catch (err: any) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pad = (n: number) => n.toString().padStart(2, "0");

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#ffffff' }}>
      <Helmet>
        <title>Creator Waitlist | AI StoryCraft Studio</title>
        <meta
          name="description"
          content="Join the AI StoryCraft Studio founding member waitlist and get first access, launch bonuses, and locked-in early pricing."
        />
        <link rel="canonical" href="https://ai-storycraft-studio.com/waitlist" />
        <meta property="og:title" content="Creator Waitlist | AI StoryCraft Studio" />
        <meta
          property="og:description"
          content="Join the AI StoryCraft Studio founding member waitlist and get first access, launch bonuses, and locked-in early pricing."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-storycraft-studio.com/waitlist" />
        <meta property="og:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Creator Waitlist | AI StoryCraft Studio" />
        <meta
          name="twitter:description"
          content="Join the AI StoryCraft Studio founding member waitlist and get first access, launch bonuses, and locked-in early pricing."
        />
        <meta name="twitter:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
      </Helmet>
      {/* Hero */}
      <section id="waitlist-top" className="max-w-3xl mx-auto px-4 pt-20 pb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight tracking-tight mb-4 text-white">
          The AI-Powered Platform That Turns Content Creators Into Business Owners
        </h1>
        <p className="text-lg mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Join the founding member waitlist — get 40% off at launch, locked in forever.
        </p>

        {/* Benefits */}
        <div className="flex flex-col items-center gap-2 mb-6">
          {[
            "40% founding member discount — locked in forever",
            "First access before public launch",
            "Exclusive founding member bonus pack",
          ].map((text) => (
            <div key={text} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#F5A623' }} />
              <span>{text}</span>
            </div>
          ))}
        </div>

        {/* Quiz CTA Section */}
        <div
          className="max-w-md mx-auto rounded-2xl p-8 mb-8"
          style={{
            background: "rgba(139, 92, 246, 0.06)",
            border: "1px solid rgba(139, 92, 246, 0.2)",
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Zap className="w-8 h-8 flex-shrink-0" style={{ color: "#8B5CF6" }} />
            <Badge className="bg-[#8B5CF6] text-white border-none text-[10px] px-2 py-0.5">FREE</Badge>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Get your free personalised creator roadmap</h3>
          <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.5)" }}>
            Answer 7 quick questions. Get a custom plan showing you exactly how to go from where you are now to making money from your content — and how long it will actually take.
          </p>
          <Link
            to="/quiz"
            className="inline-block w-full text-center font-bold text-base py-3 rounded-lg transition-colors text-white no-underline"
            style={{ backgroundColor: "#F5A623" }}
          >
            Build My Free Plan →
          </Link>
          <p className="text-xs mt-2 text-center" style={{ color: "rgba(255,255,255,0.4)" }}>
            Takes 2 minutes. No fluff. Just your plan.
          </p>
        </div>

        {/* Social proof */}
        <p className="text-center mt-4" style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)" }}>
          Join 1,200+ creators already on the waitlist
        </p>
      </section>

      {/* Tools */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-10 text-white">8 AI Tools. One Platform. Zero Guesswork.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {tools.map((t) => (
            <div
              key={t.name}
              className="rounded-xl p-5 hover:border-[rgba(255,255,255,0.2)] transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <img src={t.logo} alt={t.name} className="w-10 h-10 object-contain mb-3" />
              <h3 className="font-semibold mb-1 text-white">{t.name}</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Countdown */}
      <section className="py-16 text-center" style={{ background: '#0f0f0f' }}>
        <h2 className="text-3xl font-bold mb-8 text-white">Launching In...</h2>
        <div className="flex justify-center gap-4 md:gap-8 mb-8">
          {[
            { label: "Days", value: countdown.days },
            { label: "Hours", value: countdown.hours },
            { label: "Minutes", value: countdown.minutes },
            { label: "Seconds", value: countdown.seconds },
          ].map((u) => (
            <div key={u.label} className="text-center">
              <div className="text-4xl md:text-5xl font-bold font-mono" style={{ color: "#F5A623" }}>
                {pad(u.value)}
              </div>
              <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{u.label}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={scrollToTop}
            className="font-bold text-base px-8 py-3 rounded-lg transition-colors border-none inline-flex items-center gap-2"
            style={{ backgroundColor: "#7C3AED", color: '#ffffff' }}
          >
            <ArrowUp className="w-4 h-4" />
            Join the Waitlist
          </button>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Free to join. No credit card required.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 text-center text-sm" style={{ color: 'rgba(255,255,255,0.4)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="mb-3">Built by creators, for creators.</p>
        <div className="flex justify-center gap-4 mb-3">
          <a href="https://ai-storycraft-studio.com" className="hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>← Back to main site</a>
          <a href="https://youtube.com/@issacai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">YouTube</a>
          <a href="https://tiktok.com/@issacai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">TikTok</a>
          <a href="https://instagram.com/issacai" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Instagram</a>
        </div>
        <div className="flex justify-center gap-4">
          <Link to="/terms-of-service" className="underline hover:text-white">Terms of Service</Link>
          <Link to="/privacy-policy" className="underline hover:text-white">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}
